import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AttachMoney as SalaryIcon,
  Work as WorkIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api';
import { useCurrency } from '../../context/CurrencyContext';
import DatabaseService from '../../services/DatabaseService';
import { useAuth } from '../../context/AuthContext';
import SocketService from '../../services/SocketService';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Support for both MongoDB _id and 6-digit jobId
  const { currentUser } = useAuth();
  const [job, setJob] = useState(null);
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [applying, setApplying] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showEmailInstruction, setShowEmailInstruction] = useState(false);
  // Guard to ensure auto-apply triggers only once on /apply route
  const didAutoOpenApply = useRef(false);

  const renderStringAsListOrText = (text) => {
    const hasNewlines = /\r?\n/.test(text || '');
    const itemsByLine = String(text || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (hasNewlines && itemsByLine.length > 1) {
      return (
        <List>
          {itemsByLine.map((item, idx) => (
            <ListItem key={`str-line-${idx}`}>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
      );
    }
    const numbered = String(text || '').split(/\s*\d+\.\s*/).map(s => s.trim()).filter(Boolean);
    if (!hasNewlines && numbered.length > 1) {
      return (
        <Box component="ol" sx={{ pl: 2 }}>
          {numbered.map((item, idx) => (
            <Box component="li" key={`str-num-${idx}`} sx={{ mb: 0.5 }}>
              <Typography variant="body1">{item}</Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </Typography>
    );
  };

  useEffect(() => {
    const fetchJobDetail = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Validate ID before making API call
        if (!id || id === 'undefined') {
          throw new Error('Invalid job ID');
        }
        
        // Try to get job by ID (will work with both MongoDB _id and custom jobId)
        const response = await ApiService.getJobById(id);
        console.log('Job detail response:', response); // Debug log

        // Handle different response structures
        let jobData = response?.job || response?.data || response;
        if (!jobData) throw new Error('Job not found');

        // Normalize counters without client-side bump; trust backend to return updated values
        jobData = {
          ...jobData,
          views: jobData?.views ?? jobData?.viewCount ?? 0,
          viewCount: jobData?.viewCount ?? jobData?.views ?? 0,
          applyClicks: jobData?.applyClicks ?? jobData?.applicationCount ?? 0,
          applicationCount: jobData?.applicationCount ?? jobData?.applyClicks ?? 0,
        };
        setJob(jobData);
      } catch (error) {
        console.error('Error fetching job detail:', error);
        if (String(error?.message || '').includes('Invalid job ID')) {
          setError('Invalid job ID provided. Please check the URL and try again.');
        } else {
          setError('Failed to load job details. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    if (id && id !== 'undefined') {
      fetchJobDetail();
    } else {
      setError('Invalid job ID provided.');
      setLoading(false);
    }
  }, [id]);

  // Centralized apply flow to optionally track clicks
  const performApply = (shouldTrack = true) => {
    // Optionally track apply click immediately and reflect locally
    if (shouldTrack && id) {
      setJob(prev => (
        prev ? { ...prev, applyClicks: (prev.applyClicks ?? prev.applicationCount ?? 0) + 1 } : prev
      ));
      Promise.resolve(ApiService.trackJobApplyClick(id)).then((serverCount) => {
        if (typeof serverCount === 'number' && serverCount >= 0) {
          setJob(prev => (
            prev ? { ...prev, applyClicks: serverCount } : prev
          ));
        }
      }).catch(() => {
        // non-blocking: keep optimistic value on failure
      });
    }

    // Handle application method first: external/email should work without login gating
    const method = job?.applicationMethod || 'internal';
    if (method === 'external') {
      if (job?.externalApplicationUrl) {
        window.open(job.externalApplicationUrl, '_blank');
      } else {
        setSnackbar({
          open: true,
          message: 'No external application link provided for this job',
          severity: 'warning'
        });
      }
      return;
    }

    if (method === 'email') {
      if (job?.applicationEmail) {
        const email = job.applicationEmail;
        try {
          // Best-effort copy to clipboard for convenience
          navigator.clipboard?.writeText(email);
        } catch (e) {
          // ignore clipboard errors
        }
        setShowEmailInstruction(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Application email not available for this job',
          severity: 'warning'
        });
      }
      return;
    }

    // Internal apply requires login; keep this minimal to avoid false redirects
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: 'Please login to apply for jobs',
        severity: 'warning'
      });
      navigate('/login');
      return;
    }

    setApplyDialogOpen(true);
  };

  const handleApplyClick = () => performApply(true);

  const handleApplySubmit = async () => {
    try {
      setApplying(true);
      
      // Build resume/document attachments with graceful fallback
      const resumeUrl =
        currentUser?.jobSeekerProfile?.cv?.fileUrl ||
        currentUser?.jobSeekerProfile?.resume ||
        currentUser?.cvFileUrl ||
        `PROFILE:${currentUser?._id || 'unknown'}`;

      const additionalDocuments = [];
      if (currentUser?.jobSeekerProfile?.cv?.fileUrl) additionalDocuments.push(currentUser.jobSeekerProfile.cv.fileUrl);
      if (currentUser?.socialLinks?.portfolio) additionalDocuments.push(currentUser.socialLinks.portfolio);

      const applicationData = {
        jobId: id,
        coverLetter: coverLetter.trim(),
        appliedDate: new Date().toISOString(),
        applicantId: currentUser?._id,
        resume: resumeUrl,
        additionalDocuments
      };

      const response = await DatabaseService.createJobApplication(applicationData);
      
      // Emit real-time notification
      SocketService.emitJobApplication({
        applicationId: response.id,
        jobId: id,
        jobTitle: job.title,
        applicantName: `${currentUser.firstName} ${currentUser.lastName}`,
        applicantEmail: currentUser.email,
        applicantPhone: currentUser.phone,
        coverLetter: applicationData.coverLetter,
        resumeUrl: resumeUrl,
        employerId: job.employerId,
        appliedDate: applicationData.appliedDate
      });

      setSnackbar({
        open: true,
        message: 'Application submitted successfully!',
        severity: 'success'
      });
      
      setApplyDialogOpen(false);
      setCoverLetter('');
    } catch (error) {
      console.error('Error submitting application:', error);
      const msg = String(error?.message || '').toLowerCase().includes('already applied')
        ? 'You have already applied to this job'
        : 'Failed to submit application. Please try again.';
      setSnackbar({
        open: true,
        message: msg,
        severity: msg.includes('already applied') ? 'warning' : 'error'
      });
    } finally {
      setApplying(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Auto-open apply when visiting /jobs/:id/apply (guarded against loops)
  useEffect(() => {
    if (
      job &&
      typeof location?.pathname === 'string' &&
      location.pathname.endsWith('/apply') &&
      !didAutoOpenApply.current
    ) {
      didAutoOpenApply.current = true;
      // Replicate apply flow (no tracking) to avoid dependency on performApply
      const method = job?.applicationMethod || 'internal';
      if (method === 'external') {
        if (job?.externalApplicationUrl) {
          window.open(job.externalApplicationUrl, '_blank');
        } else {
          setSnackbar({ open: true, message: 'No external application link provided for this job', severity: 'warning' });
        }
        return;
      }
      if (method === 'email') {
        if (job?.applicationEmail) {
          const email = job.applicationEmail;
          try { navigator.clipboard?.writeText(email); } catch (_) {}
          setShowEmailInstruction(true);
        } else {
          setSnackbar({ open: true, message: 'Application email not available for this job', severity: 'warning' });
        }
        return;
      }
      // internal
      if (!currentUser) {
        setSnackbar({ open: true, message: 'Please login to apply for jobs', severity: 'warning' });
        navigate('/login');
        return;
      }
      setApplyDialogOpen(true);
    }
  }, [job, location?.pathname, currentUser, navigate]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" align="center">
          Job not found
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {job.title}
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item>
              <Chip
                icon={<BusinessIcon />}
                label={job.company}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<LocationIcon />}
                label={(function(){
                  const normalizeCountry = (c) => (c || '').replace(/\bUSA\b/i, 'Zambia');
                  if (typeof job.location === 'object') {
                    const city = job.location.city || '';
                    const country = normalizeCountry(job.location.country || '');
                    return `${city}${city && country ? ', ' : ''}${country}`.replace(/^,\s*|,\s*$/g, '');
                  }
                  const asString = job.location || 'N/A';
                  return asString.replace(/\bUSA\b/i, 'Zambia');
                })()}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<WorkIcon />}
                label={job.jobType}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<CategoryIcon />}
                label={job.category}
                variant="outlined"
              />
            </Grid>
          </Grid>
          {/* Live stats */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid item>
              <Chip icon={<VisibilityIcon />} label={`${job.views ?? job.viewCount ?? 0} views`} variant="outlined" />
            </Grid>
            <Grid item>
              <Chip icon={<SendIcon />} label={`${job.applyClicks ?? job.applicationCount ?? 0} apply clicks`} variant="outlined" />
            </Grid>
          </Grid>
          <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SalaryIcon />
            {job.salary && typeof job.salary === 'object' 
              ? `${formatCurrency(job.salary.min)} - ${formatCurrency(job.salary.max)}${job.salary.period ? ` per ${job.salary.period}` : ''}${job.salary.negotiable ? ' (Negotiable)' : ''}`
              : job.salary || 'Salary not specified'}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Job Description
          </Typography>
          <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {job.description}
          </Typography>
        </Box>

        {/* Requirements Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Requirements
          </Typography>
          <List>
            {(job.requirements?.skills?.length > 0 || (Array.isArray(job.skills) && job.skills.length > 0)) && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Required Skills" 
                  secondary={(job.requirements?.skills || job.skills).join(', ')}
                />
              </ListItem>
            )}
            {job.requirements?.experience && (job.requirements.experience.min != null || job.requirements.experience.max != null) && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Experience Required" 
                  secondary={
                    job.requirements.experience.min != null && job.requirements.experience.max != null
                      ? `${job.requirements.experience.min} - ${job.requirements.experience.max} years`
                      : job.requirements.experience.min != null
                        ? `${job.requirements.experience.min}+ years`
                        : `Up to ${job.requirements.experience.max} years`
                  }
                />
              </ListItem>
            )}
            {job.requirements?.education && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Education" 
                  secondary={job.requirements.education.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                />
              </ListItem>
            )}
            {job.requirements?.languages && job.requirements.languages.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Languages" 
                  secondary={job.requirements.languages.join(', ')}
                />
              </ListItem>
            )}
            {job.requirements?.certifications && job.requirements.certifications.length > 0 && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Certifications" 
                  secondary={job.requirements.certifications.join(', ')}
                />
              </ListItem>
            )}
            {(!job.requirements || 
              (!job.requirements.skills?.length && 
               !job.requirements.experience && 
               !job.requirements.education && 
               !job.requirements.languages?.length && 
               !job.requirements.certifications?.length)) && (
              <ListItem>
                <ListItemText primary="No specific requirements listed" />
              </ListItem>
            )}
          </List>
          {typeof job.requirements === 'string' && job.requirements.trim() && (
            <Box sx={{ mt: 2 }}>
              {renderStringAsListOrText(job.requirements)}
            </Box>
          )}
        </Box>

        {/* Responsibilities Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Responsibilities
          </Typography>
          {Array.isArray(job.responsibilities) && job.responsibilities.length > 0 ? (
            <List>
              {job.responsibilities.map((responsibility, index) => (
                <ListItem key={`responsibility-${index}`}>
                  <ListItemIcon>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={responsibility} />
                </ListItem>
              ))}
            </List>
          ) : typeof job.responsibilities === 'string' && job.responsibilities.trim() ? (
            renderStringAsListOrText(job.responsibilities)
          ) : (
            <List>
              <ListItem>
                <ListItemText primary="No specific responsibilities listed" />
              </ListItem>
            </List>
          )}
        </Box>

        {/* Benefits Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Benefits
          </Typography>
          {Array.isArray(job.benefits) && job.benefits.length > 0 ? (
            <List>
              {job.benefits.map((benefit, index) => (
                <ListItem key={`benefit-${index}`}>
                  <ListItemIcon>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
                </ListItem>
              ))}
            </List>
          ) : typeof job.benefits === 'string' && job.benefits.trim() ? (
            renderStringAsListOrText(job.benefits)
          ) : (
            <List>
              <ListItem>
                <ListItemText primary="No benefits specified" />
              </ListItem>
            </List>
          )}
        </Box>

        {/* Additional Information */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" />
                Posted: {job.postedDate}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" />
                Application Deadline: {job.applicationDeadline}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Email Apply Instructions (shown after clicking Apply) */}
        {showEmailInstruction && job?.applicationMethod === 'email' && job?.applicationEmail && (
          <Alert severity="info" sx={{ my: 2 }}>
            Apply by email: send your cover letter and CV to <strong>{job.applicationEmail}</strong>.
          </Alert>
        )}

        {/* Apply Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            color="primary"
            sx={{ minWidth: 200 }}
            onClick={handleApplyClick}
          >
            {job?.applicationMethod === 'external' ? 'Apply on External Site' : job?.applicationMethod === 'email' ? 'Apply via Email' : 'Apply Now'}
          </Button>
        </Box>

        {/* Application Dialog */}
        <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Apply for {job?.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Write a cover letter to introduce yourself and explain why you're interested in this position.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Cover Letter"
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Dear Hiring Manager,

I am writing to express my interest in the position..."
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApplyDialogOpen(false)} disabled={applying}>
              Cancel
            </Button>
            <Button 
              onClick={handleApplySubmit} 
              variant="contained" 
              disabled={applying || !coverLetter.trim()}
            >
              {applying ? 'Submitting...' : 'Submit Application'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default JobDetail;